import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDebounceValue, useWindowSize } from "usehooks-ts";
import { INVALID_MOBILE_PATHS, SCREEN_WIDTH_THRESHOLD } from "../constants";

export const MobileMessageDialogWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { width } = useWindowSize();
  const location = useLocation();
  const [debouncedWidth] = useDebounceValue(width, 250);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Memoize the path check to avoid recalculating on every render
  const isValidPath = useMemo(
    () => !INVALID_MOBILE_PATHS.includes(location.pathname),
    [location.pathname],
  );

  const shouldShowMobileDialog =
    isValidPath && debouncedWidth < SCREEN_WIDTH_THRESHOLD;

  useEffect(() => {
    // Get root element only once and cache it
    if (!rootRef.current) {
      rootRef.current = document.getElementById("root") as HTMLDivElement;
    }

    const root = rootRef.current;
    if (!root) return;

    root.style.display = shouldShowMobileDialog ? "none" : "block";

    // This wrapper only mounts under BaseLayout, and some routes — /pages/wizard
    // among them — render outside it. Without this cleanup, navigating from a
    // hidden mobile page to one of those routes unmounts the only code that
    // could un-hide #root, leaving the app blank. Clearing the inline value
    // (rather than forcing "block") hands control back to the stylesheet.
    return () => {
      root.style.display = "";
    };
  }, [shouldShowMobileDialog]);

  // Only render children when mobile dialog should be shown
  if (shouldShowMobileDialog) return <>{children}</>;

  return null;
};
